import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Heart, CreditCard, ShieldCheck, Building2, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { BANK_DETAILS } from "../lib/branches";

export default function Giving() {
  const [loading, setLoading] = useState("");
  const [copied, setCopied] = useState("");

  const copy = (label, value) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(""), 1500);
  };

  const handleSelect = async (provider) => {
    setLoading(provider);
    try {
      await api.post("/giving/log", { provider });
      toast.info(
        `${provider === "payfast" ? "PayFast" : "Yoco"} checkout will be enabled soon. Thank you for your heart to give!`,
        { duration: 4000 }
      );
    } catch {
      // ignore
    } finally {
      setLoading("");
    }
  };

  return (
    <div data-testid="giving-page">
      <section className="bg-olive text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="overline text-terracotta mb-4">Give</p>
            <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
              Sow into the <em className="italic font-light text-terracotta">Kingdom.</em>
            </h1>
            <p className="mt-6 text-bone/80 text-lg max-w-xl">
              Every seed you plant builds the house of God. Choose your preferred giving method
              below.
            </p>
            <blockquote className="mt-8 pl-5 border-l-2 border-terracotta text-bone/85 italic max-w-xl">
              "Give, and it will be given to you. A good measure, pressed down, shaken together
              and running over…" — Luke 6:38
            </blockquote>
          </div>
          <div className="md:col-span-5 md:pl-6">
            <div className="bg-bone/10 backdrop-blur-sm p-8 border border-bone/20">
              <div className="flex items-center gap-3 text-bone/90 mb-4">
                <ShieldCheck size={20} className="text-terracotta" />
                <p className="text-sm">Secure giving via certified SA providers</p>
              </div>
              <div className="flex items-center gap-3 text-bone/90">
                <Heart size={20} className="text-terracotta" />
                <p className="text-sm">100% goes to the mission and local work</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BANK DETAILS (EFT) — NOW AT TOP */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20" data-testid="bank-details-section">
        <div className="flex items-center gap-3 mb-3">
          <Building2 size={20} className="text-terracotta" />
          <p className="overline text-terracotta">Direct Bank Transfer (EFT)</p>
        </div>
        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-olive">
          Prefer to give via EFT?
        </h2>
        <p className="mt-3 text-ink/75 max-w-2xl">
          Use our banking details below to give directly from your own banking app. Please use
          your name + "Tithe" or "Offering" as the reference.
        </p>

        <div className="mt-8 bg-white border border-border divide-y divide-border">
          {[
            { label: "Account name", value: BANK_DETAILS.account_name },
            { label: "Bank", value: BANK_DETAILS.bank },
            { label: "Account number", value: BANK_DETAILS.account_number },
            { label: "Branch code", value: BANK_DETAILS.branch_code },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between p-5 hover:bg-bone transition-colors">
              <div>
                <p className="overline text-muted_ink mb-1">{row.label}</p>
                <p className="font-heading font-bold text-lg text-ink tracking-tight">{row.value}</p>
              </div>
              <button
                onClick={() => copy(row.label, row.value)}
                data-testid={`copy-${row.label.toLowerCase().replace(" ", "-")}`}
                className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-border hover:border-olive hover:text-olive flex items-center gap-2 transition-colors"
              >
                {copied === row.label ? <Check size={14} /> : <Copy size={14} />}
                {copied === row.label ? "Copied" : "Copy"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* PAYMENT GATEWAYS — NOW BELOW EFT */}
      <section className="bg-cream border-y border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <p className="overline text-terracotta mb-3">Online Payment</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-olive mb-10">
            Or give online via card
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <button
              data-testid="payfast-option-btn"
              onClick={() => handleSelect("payfast")}
              disabled={loading === "payfast"}
              className="text-left p-8 bg-white border border-border hover:border-olive hover:shadow-lg hover:-translate-y-1 transition-all group disabled:opacity-60"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-olive text-bone flex items-center justify-center">
                  <CreditCard size={24} />
                </div>
                <span className="overline text-terracotta">SA Payments</span>
              </div>
              <h3 className="font-heading font-black text-3xl text-ink">PayFast</h3>
              <p className="text-muted_ink mt-3 leading-relaxed">
                South Africa's trusted online payment gateway. EFT, card, and instant EFT supported.
              </p>
              <p className="mt-8 text-terracotta font-semibold group-hover:translate-x-1 transition-transform">
                Give via PayFast →
              </p>
            </button>

            <button
              data-testid="yoco-option-btn"
              onClick={() => handleSelect("yoco")}
              disabled={loading === "yoco"}
              className="text-left p-8 bg-terracotta text-white border border-terracotta hover:bg-terracotta-hover hover:-translate-y-1 transition-all group disabled:opacity-60"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-white/15 flex items-center justify-center">
                  <CreditCard size={24} />
                </div>
                <span className="overline text-white/80">SA Payments</span>
              </div>
              <h3 className="font-heading font-black text-3xl">Yoco</h3>
              <p className="text-white/85 mt-3 leading-relaxed">
                Pay securely with card via Yoco — fast, simple, and trusted by thousands of
                South African businesses.
              </p>
              <p className="mt-8 font-semibold group-hover:translate-x-1 transition-transform">
                Give via Yoco →
              </p>
            </button>
          </div>

          <p className="mt-10 text-xs text-muted_ink italic">
            * Online payment integrations are being finalized. Click a method above to register your interest.
          </p>
        </div>
      </section>
    </div>
  );
}
